import { useCallback } from 'react';
import { useSweepApi } from '../sweep/useSweepApi';
import type { OpenAiResponse } from '../../components/parser/ParserTypes';
import type {
  BaseElementProperties,
  LayoutProperties,
  SearchResult,
  ContentSearchResult,
  ConsumersOrDependencies,
} from '@server/documentation';
import type { ObjectParsedDataByType } from '@server/parser';
import type { DocumentationData } from '../../types/documentation/documentation';
import type { NameProperties } from '../../components/parser/ParserTypes';
import type { SearchResponseWithConfigurationItems } from '../../components/documentation/universal-search/types';
import type { DocCardChatResponse } from '../../components/documentation/types';
import buildURLQuery from '../../lib/buildURLQuery';
import type { AiChatConfigurationContext, AiChatFieldContext } from '@server/ai';
import { createConfigurationItemFromParserResponseItem } from '../../components/parser/parserUtils';

interface AiChatBasePayload {
  sessionId: string;
  message: string;
  isFirstMessage: boolean;
  crmOrgId: string;
}

export interface AiChatFieldPayload extends AiChatBasePayload {
  context: AiChatFieldContext;
}

export interface AiChatConfigurationPayload extends AiChatBasePayload {
  context: AiChatConfigurationContext;
}

const transformSearchResponse = (
  response: ContentSearchResult | SearchResult,
): SearchResponseWithConfigurationItems => {
  const _response = response;
  delete _response?.files; //by product decision files are not supported atm

  return Object.entries(_response).reduce((acc, [key, value]) => {
    acc[key as keyof SearchResponseWithConfigurationItems] =
      value?.map((item) => {
        const metadataId = 'metadataId' in item ? item.metadataId : '';
        const namespace = 'namespace' in item ? item.namespace : '';
        const objectName = 'objectName' in item ? (item.objectName ?? '') : '';

        return createConfigurationItemFromParserResponseItem({
          keyType: key,
          responseItem: {
            ...item,
            metadataId,
            namespace,
            type: key,
          },
          objectApiName: objectName,
        });
      }) ?? [];
    return acc;
  }, {} as SearchResponseWithConfigurationItems);
};

export const useDocumentationApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    get_salesforceDocumentation: useCallback(
      async (crmOrgId: string): Promise<DocumentationData> => {
        const response = await sweepApi.get(`/documentation-salesforce/${crmOrgId}`);
        return response.data;
      },
      [sweepApi],
    ),
    get_salesforceObjectTypes: useCallback(
      async (crmOrgId: string): Promise<NameProperties[]> => {
        const response = await sweepApi.get<NameProperties[]>(
          `/documentation-salesforce/${crmOrgId}/objects`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_createObject: useCallback(
      async (crmOrgId: string, objectName: string, favorite: boolean) => {
        const response = await sweepApi.post<ObjectParsedDataByType>(
          `/documentation-salesforce/${crmOrgId}/objects/${objectName}`,
          { favorite },
        );
        return response.data;
      },
      [sweepApi],
    ),
    patch_toggleObjectFavorite: useCallback(
      async (crmOrgId: string, objectName: string, favorite: boolean) => {
        return await sweepApi.patch(`/documentation-salesforce/${crmOrgId}/objects/${objectName}`, {
          favorite,
        });
      },
      [sweepApi],
    ),
    get_metadataTypes: useCallback(
      async (crmOrgId: string): Promise<string[]> => {
        const response = await sweepApi.get<string[]>(
          `/documentation-salesforce/${crmOrgId}/metadataTypes`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    get_metadataData: useCallback(
      async (crmOrgId: string, metadataTypeName: string) => {
        const response = await sweepApi.get<BaseElementProperties[]>(
          `/documentation-salesforce/${crmOrgId}/metadataTypes/${metadataTypeName}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_layout: useCallback(
      async (crmOrgId: string, metadataId: string) => {
        const response = await sweepApi.get<LayoutProperties>(
          `/documentation-salesforce/${crmOrgId}/layouts/${metadataId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationDependenciesFromSalto: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const { data } = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation-salesforce/${crmOrgId}/configuration/dependencies?metadataId=${encodeURIComponent(metadataId)}`,
        );

        return data;
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationConsumersFromSalto: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const response = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation-salesforce/${crmOrgId}/configuration/consumers?metadataId=${encodeURIComponent(metadataId)}`,
        );

        return response.data;
      },
      [sweepApi],
    ),
    get_crmOrgFieldConsumerFromSalto: useCallback(
      async ({
        crmOrgId,
        objectName,
        fieldName,
        metadataId,
      }: {
        crmOrgId: string;
        objectName: string;
        fieldName: string;
        metadataId: string;
      }) => {
        const searchParams = buildURLQuery({
          objectName,
          fieldName,
          activeOnly: false,
          metadataId,
        });
        const response = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation-salesforce/${crmOrgId}/field-consumers?${searchParams}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_aiConfigurationChat: useCallback(
      async (payload: AiChatConfigurationPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(
          `/documentation-salesforce/${crmOrgId}/ai-chat`,
          _payload,
        );
        return response.data as DocCardChatResponse;
      },
      [sweepApi],
    ),
    post_aiFieldChat: useCallback(
      async (payload: AiChatFieldPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(
          `/documentation-salesforce/${crmOrgId}/ai-chat`,
          _payload,
        );
        return response.data as DocCardChatResponse;
      },
      [sweepApi],
    ),
    post_crmOrgGenerateConfigurationGpt: useCallback(
      async ({ crmOrgId, payload }: { crmOrgId: string; payload: { metadataId: string } }) => {
        const response = await sweepApi.post(
          `/documentation-salesforce/${crmOrgId}/generate-configuration-gpt`,
          payload,
        );
        return response.data as OpenAiResponse;
      },
      [sweepApi],
    ),
    get_searchNameAndLabel: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation-salesforce/${crmOrgId}/search?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data);
      },
      [sweepApi],
    ),
    get_searchContent: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation-salesforce/${crmOrgId}/search-content?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data);
      },
      [sweepApi],
    ),
    get_NaclFile: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const response = await sweepApi.get(
          `/documentation-salesforce/${crmOrgId}/nacl-file?metadataId=${metadataId}`,
        );
        return response.data as string;
      },
      [sweepApi],
    ),
  };
};
